import { css, FlattenSimpleInterpolation } from 'styled-components/macro';
import { EPassengerCategory, IDiscountCard } from '../../../common/types';
import {
  IPassenger,
  ISearchCity,
  ISearchParameters,
} from '../../../contexts/Search/types';
import { EDatePickerModifiedDate } from '../../DatePicker/types';
import FunelModal from '../../Funnel/Modal';
import {
  IHoveredCitiesIndex,
  IPopularCities,
  ISuggestedCities,
} from '../types';
import CitiesPanel from './CitiesPanel';
import DatesPanel from './DatesPanel';
import PassengersPanel from './PassengersPanel';

export enum ESearchPanel {
  DEPARTURE_DATE = 'DEPARTURE_DATE',
  RETURN_DATE = 'RETURN_DATE',

  ORIGIN_CITY = 'ORIGIN_CITY',
  DESTINATION_CITY = 'DESTINATION_CITY',

  PASSENGERS = 'PASSENGERS',
}

interface SearchPanelProps {
  displayedPanel: ESearchPanel | null;
  onClose: () => void;

  searchParameters: ISearchParameters;

  popularCities: IPopularCities;
  suggestedCities: ISuggestedCities;

  setOutboundDate: (date: string, from: number) => void;
  setReturnDate: (date: string, from: number) => void;

  onSelectOriginCity: (city: ISearchCity) => void;
  onSelectDestinationCity: (city: ISearchCity) => void;

  onAddPassenger: (category: EPassengerCategory) => void;
  onRemovePassenger: (id: number) => void;
  onUpdatePassenger: (updatedPassenger: IPassenger) => void;
  setPassengers: (passengers: IPassenger[]) => void;

  getDiscountCards: () => Promise<IDiscountCard[]>;

  onOriginCityChange: (city: string) => void;
  onDestinationCityChange: (city: string) => void;
  focusInput: (input: ESearchPanel) => void;
  customStyle?: FlattenSimpleInterpolation;
  hoveredCitiesIndex: IHoveredCitiesIndex;
  closePanelButtonRef: React.MutableRefObject<HTMLButtonElement>;
  passengersFormRef: React.MutableRefObject<HTMLFormElement>;
}

/**
 * @description Returns a component that display a panel for modifying search bar inputs
 * @returns {React.FC<SearchPanelProps>}
 */
const SearchPanel: React.FC<SearchPanelProps> = ({
  closePanelButtonRef,
  customStyle,
  displayedPanel,
  focusInput,
  getDiscountCards,
  hoveredCitiesIndex,
  onAddPassenger,
  onClose,
  onDestinationCityChange,
  onOriginCityChange,
  onRemovePassenger,
  onSelectDestinationCity,
  onSelectOriginCity,
  onUpdatePassenger,
  passengersFormRef,
  popularCities,
  searchParameters,
  setOutboundDate,
  setPassengers,
  setReturnDate,
  suggestedCities,
}) => {
  if (!displayedPanel) {
    return null;
  }

  return (
    <FunelModal
      containerStyle={customStyle}
      withoutDesktopModal
      wrapperStyle={css`
        max-height: 100% !important;
      `}
    >
      {displayedPanel === ESearchPanel.ORIGIN_CITY && (
        <CitiesPanel
          city={searchParameters.origin}
          closePanelButtonRef={closePanelButtonRef}
          hoveredCityIndex={hoveredCitiesIndex[ESearchPanel.ORIGIN_CITY]}
          isOrigin
          onCityChange={onOriginCityChange}
          onClose={onClose}
          onSelectCity={onSelectOriginCity}
          popularCities={popularCities[ESearchPanel.ORIGIN_CITY]}
          suggestedCities={suggestedCities[ESearchPanel.ORIGIN_CITY]}
        />
      )}

      {displayedPanel === ESearchPanel.DESTINATION_CITY && (
        <CitiesPanel
          city={searchParameters.destination}
          closePanelButtonRef={closePanelButtonRef}
          hoveredCityIndex={hoveredCitiesIndex[ESearchPanel.DESTINATION_CITY]}
          onCityChange={onDestinationCityChange}
          onClose={onClose}
          onSelectCity={onSelectDestinationCity}
          popularCities={popularCities[ESearchPanel.DESTINATION_CITY]}
          suggestedCities={suggestedCities[ESearchPanel.DESTINATION_CITY]}
        />
      )}

      {displayedPanel === ESearchPanel.DEPARTURE_DATE && (
        <DatesPanel
          closePanelButtonRef={closePanelButtonRef}
          focusInput={focusInput}
          modifiedDate={EDatePickerModifiedDate.START}
          onClose={onClose}
          searchParameters={searchParameters}
          setOutboundDate={setOutboundDate}
          setReturnDate={setReturnDate}
        />
      )}

      {displayedPanel === ESearchPanel.RETURN_DATE && (
        <DatesPanel
          closePanelButtonRef={closePanelButtonRef}
          focusInput={focusInput}
          modifiedDate={EDatePickerModifiedDate.END}
          onClose={onClose}
          searchParameters={searchParameters}
          setOutboundDate={setOutboundDate}
          setReturnDate={setReturnDate}
        />
      )}

      {displayedPanel === ESearchPanel.PASSENGERS && (
        <PassengersPanel
          closePanelButtonRef={closePanelButtonRef}
          formRef={passengersFormRef}
          getDiscountCards={getDiscountCards}
          onAddPassenger={onAddPassenger}
          onClose={onClose}
          onRemovePassenger={onRemovePassenger}
          onUpdatePassenger={onUpdatePassenger}
          passengers={searchParameters.passengers}
          setPassengers={setPassengers}
        />
      )}
    </FunelModal>
  );
};

export default SearchPanel;
